import { DashboardCard } from "@/components/design-system/dashboard-card";
import AppLayout from "@/components/layout/AppLayout";
import {
  fetchSchoolDashboardData,
  selectSchoolDashboardData,
  selectSchoolDashboardLoading,
} from "@/store/slices/schoolDashboardSlice";
import { AppDispatch } from "@store";
import { ChevronDown, ChevronUp, GraduationCap } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import SchoolDashboardStudentListSection from "./components/school-dashboard-lits";
import {
  selectQuery,
  selectStudentLoading,
  selectTotalItems,
} from "@/store/slices/studentSlice";
import { isAllSchoolsSelected, selectSchoolId, selectUserRole } from "@/store/slices/authSlice";
import { ROLES } from "@/utils/constants";

function SchoolDashboardPage() {
  const dispatch = useDispatch<AppDispatch>();
  const dashboardData = useSelector(selectSchoolDashboardData);
  const loading = useSelector(selectSchoolDashboardLoading);
  const location = useLocation();
  const navigate = useNavigate();
  const schoolId = useSelector(selectSchoolId);
  const isAllSchools = useSelector(isAllSchoolsSelected);
  const userRole = useSelector(selectUserRole);

  const studentTotalItems = useSelector(selectTotalItems);
  const studentQuery = useSelector(selectQuery);

  const studentLoading = useSelector(selectStudentLoading);

  // Get the selected card from URL query parameters or default to "students"
  const queryParams = new URLSearchParams(location.search);
  const cardFromUrl = queryParams.get("card");

  // Set default selected card based on URL or default to "students"
  const [selectedCard, setSelectedCard] = useState<string | null>(
    cardFromUrl || "students"
  );

  useEffect(() => {
    // If the card in URL is not valid, reset to "students"
    if (schoolId && !isAllSchools) {
      dispatch(fetchSchoolDashboardData(schoolId));
    } else {
      dispatch(fetchSchoolDashboardData());
    }
  }, [schoolId, isAllSchools]);

  const [showAllGrades, setShowAllGrades] = useState(false);

  const visibleGrades = showAllGrades
    ? dashboardData?.grades
    : dashboardData?.grades?.slice(0, 1);

  // Fetch school dashboard data on component mount
  // useEffect(() => {
  //   dispatch(fetchSchoolDashboardData());
  // }, [dispatch]);

  // Update URL when selected card changes
  const handleCardSelection = (cardId: string) => {
    setSelectedCard(cardId);

    // Update URL query parameter
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.set("card", cardId);
    navigate({ search: newQueryParams.toString() }, { replace: true });
  };

  const gradeName =
    selectedCard !== "students"
      ? dashboardData?.grades?.find(
        (grade: { id: string; grade: string; count: number }) =>
          grade.id === selectedCard
      )?.grade || ""
      : "";

  const studentCount =
    selectedCard === "students" && studentQuery.fromDate && studentQuery.toDate
      ? studentTotalItems
      : dashboardData?.total_students;

  const getGradeCount = (gradeId: string) => {
    if (
      gradeId === studentQuery.gradeId &&
      selectedCard === gradeId &&
      !studentQuery.search
    ) {
      return studentTotalItems;
    }
    return dashboardData?.grades?.find(
      (grade: { id: string; grade: string; count: number }) =>
        grade.id === gradeId
    )?.count;
  };

  return (
    <AppLayout>
      <div className="flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-shrink-0 mb-[35px]">
          {/* Total Students Card */}
          <DashboardCard
            onClick={() => handleCardSelection("students")}
            title="Total No of Students On-Boarded"
            index={2}
            value={loading ? "" : studentCount || "0"}
            icon={<GraduationCap className="w-[26px] h-[26px]" />}
            waveColor="blue"
            isActive={selectedCard === "students"}
            loading={selectedCard === "students" && studentLoading}
          />

          {/* Grade-specific Cards */}
          {visibleGrades?.map(
            (grade: { id: string; grade: string; count: number }) => (
              <DashboardCard
                key={grade.id}
                onClick={() => handleCardSelection(`${grade.id}`)}
                title={`Total No of Students in ${grade.grade}`}
                index={2}
                value={loading ? "" : getGradeCount(grade.id) || "0"}
                icon={<GraduationCap className="w-[24px] h-[24px]" />}
                waveColor="blue"
                isActive={selectedCard === `${grade.id}`}
                loading={selectedCard === `${grade.id}` && studentLoading}
              />
            )
          )}
          {/* Show More / Show Less Button */}
          {dashboardData?.grades?.length > 1 && (
            <DashboardCard
              onClick={() => setShowAllGrades(!showAllGrades)}
              title={showAllGrades ? "Show Less" : "Show More"}
              value=""
              icon={showAllGrades ? <ChevronUp className="w-[24px] h-[24px]" /> : <ChevronDown className="w-[24px] h-[24px]" />}
              waveColor="blue"
            />
          )}
        </div>

        {/* Students List - Takes remaining height */}
        <div className="min-h-[40rem]">
          <SchoolDashboardStudentListSection
            schoolId={schoolId || dashboardData?.id}
            gradeName={gradeName}
            gradeId={selectedCard !== "students" ? selectedCard || "" : ""}
          />
        </div>
      </div>
    </AppLayout>
  );
}

export default SchoolDashboardPage;
